// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import { syncFCMToken, setupForegroundNotifications, startTokenHeartbeat } from './utils/fcm';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import SearchProviders from './pages/SearchProviders';
import ProviderProfile from './pages/ProviderProfile';
import ManageOrders from './pages/ManageOrders';
import { Toaster } from './components/ui/sonner';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import ArticlePage from './pages/ArticlePage';
import AdminArticlePage from './pages/AdminArticlePage';
import Notifications from './components/Notifications';
import CategoriesList from './pages/CategoriesList';
import CategoryHub from './pages/CategoryHub';
import CategoryLocation from './pages/CategoryLocation';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import './index.css';

const queryClient = new QueryClient();

function AppContent({ darkMode, toggleDarkMode, chatSocket, setChatSocket }) {
  const navigate = useNavigate();
  const location = useLocation();

  // FCM Setup (keep your existing logic)
  useEffect(() => {
    let stopHeartbeat;
    let unsubscribe;
    const setupFCM = async () => {
      try {
        await syncFCMToken();
        stopHeartbeat = await startTokenHeartbeat();
        unsubscribe = setupForegroundNotifications(navigate);
      } catch (err) {
        console.error('[App] Error in FCM setup:', err);
      }
    };
    setupFCM();
    return () => {
      if (unsubscribe) unsubscribe();
      if (stopHeartbeat) stopHeartbeat();
    };
  }, [navigate]);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-HDE3BPR4FP', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <div className="App">
        <Toaster position="top-center" richColors closeButton />
        <Notifications userId={undefined} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/search" element={<SearchProviders />} />
          <Route path="/provider/:providerId" element={<ProviderProfile />} />
          <Route path="/manage-orders" element={<ManageOrders />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/categories" element={<CategoriesList />} />
          <Route path="/categories/:slug" element={<CategoryHub />} />
          <Route path="/categories/:slug/:location" element={<CategoryLocation />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<ArticlePage />} />
          <Route path="/blog/new" element={<AdminArticlePage />} />
          <Route path="/blog/edit/:id" element={<AdminArticlePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  const [chatSocket, setChatSocket] = useState(null);
  return (
    <QueryClientProvider client={queryClient}>
      <Router basename="/">
        <AuthProvider>
          <ThemeProvider>
            <AppContent 
              chatSocket={chatSocket} 
              setChatSocket={setChatSocket} 
            />
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
